import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import { useHttpClient } from '../../client/HttpClientContext';
import { Metrics } from '../../metrics/Metrics';
import { useItemRegistry } from './useItemRegistry';
import { useAutosaveWindowEvents } from './useAutosaveWindowEvents';
import { useAutosaveStatus } from './useAutosaveStatus';
import { groupByStrategyType } from '../strategies/strategy';
export const useAutosaveCoreV2 = ({
  strategies
}) => {
  const httpClient = useHttpClient();
  const {
    status,
    statusHandlers
  } = useAutosaveStatus();
  const handlersRef = useRef(new Map());
  const [lastSavedItems, setLastSavedItems] = useState([]);
  const itemRegistry = useItemRegistry();
  const inFlightSaveCount = useRef(0);
  const getHasInFlightSaves = useCallback(() => inFlightSaveCount.current > 0, [inFlightSaveCount]);
  useAutosaveWindowEvents(getHasInFlightSaves);
  const undo = useCallback(() => {
    statusHandlers.undoStarted();
    const groupedItems = groupByStrategyType(lastSavedItems);
    const undoForStrategy = (strategyType, strategyItems) => {
      return strategies[strategyType].undo(strategyItems, httpClient);
    };
    Object.entries(groupedItems).filter(([__, items]) => items.length > 0).map(([strategyType, items]) => undoForStrategy(strategyType, items));
  }, [statusHandlers, lastSavedItems, strategies, httpClient]);
  const save = useCallback(items => {
    inFlightSaveCount.current++;
    statusHandlers.savingStarted();
    const groupedItems = groupByStrategyType(items);
    const saveForStrategy = (strategyType, strategyItems) => {
      return strategies[strategyType].save(strategyItems, httpClient);
    };
    const savePromises = Object.entries(groupedItems).filter(([__, strategyItems]) => strategyItems.length > 0).map(([strategyType, strategyItems]) => saveForStrategy(strategyType, strategyItems));
    Promise.allSettled(savePromises).then(results => {
      const allSuccess = results.every(result => result.status === 'fulfilled' && result.value);
      if (allSuccess) {
        statusHandlers.savingSucceeded();
      } else {
        statusHandlers.savingFailed();
      }
    }).catch(() => {}).finally(() => {
      setLastSavedItems(items);
      inFlightSaveCount.current--;
    });
  }, [statusHandlers, strategies, httpClient]);
  const saveValidRequests = useCallback(() => {
    const handlers = Array.from(handlersRef.current.values());
    const itemsToProcess = itemRegistry.getAll().filter(item => handlers.every(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => !getIsHandlerForRequest(item.data.request) || getIsValidRequest(item.data.request)));
    if (itemsToProcess.length > 0) {
      itemRegistry.filter(item => !itemsToProcess.includes(item));
      save(itemsToProcess);
    }
  }, [save, itemRegistry]);
  const cancelRequestsForSource = useCallback(sourceId => {
    if (itemRegistry.hasItem(sourceId)) {
      itemRegistry.remove(sourceId);
    }
  }, [itemRegistry]);
  const clearPendingRequests = useCallback(() => {
    const items = itemRegistry.getAll();
    itemRegistry.clear();
    for (const item of items) {
      item.data.onClear();
    }
    statusHandlers.requestsCleared();
    Metrics.counter('autosave-clear-pending-requests').increment();
  }, [itemRegistry, statusHandlers]);
  const enqueueRequest = useCallback(operation => {
    itemRegistry.add({
      id: operation.request.sourceId,
      data: operation,
      timestamp: Date.now()
    });
  }, [itemRegistry]);
  const registerHandler = useCallback(config => {
    handlersRef.current.set(config.id, config);
    return () => {
      handlersRef.current.delete(config.id);
    };
  }, []);
  const items = itemRegistry.getAll();
  const requests = useMemo(() => items.map(item => item.data.request), [items]);
  const invalidRequests = useMemo(() => {
    const handlers = Array.from(handlersRef.current.values());
    return items.filter(item => handlers.some(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => getIsHandlerForRequest(item.data.request) && !getIsValidRequest(item.data.request))).map(item => item.data.request);
  }, [items]);
  return {
    cancelRequestsForSource,
    clearPendingRequests,
    enqueueRequest,
    hasRequestForSource: itemRegistry.hasItem,
    invalidRequests,
    lastSavedItems,
    registerHandler,
    requests,
    saveValidRequests: useMemo(() => debounce(saveValidRequests, 25), [saveValidRequests]),
    status,
    undo
  };
};